import React, { useState, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';
import { useAuth } from '@/store/authStore';
import api from '@/services/api/client';
import { TruckIcon, SignalIcon, SignalSlashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface Vehicle {
  id: number;
  registration_number: string;
  /** API returnerar last_position från Redis (realtidsdata), inte last_position_time */
  last_position?: {
    timestamp: string;
    lat: number;
    lng: number;
    speed?: number;
    heading?: number;
  };
}

const VehicleListWidget: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isGlobalAdmin = user?.is_global_admin === true;
  const organizationId = isGlobalAdmin ? undefined : user?.organization_id;

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '5000', includeSubOrgs: 'true' });
      if (organizationId) params.set('organizationId', organizationId);
      const response = await api.get(`/api/vehicles?${params.toString()}`);
      if (response.data.success) {
        setVehicles(response.data.data);
      } else {
        setError(response.data.error || 'Failed to load vehicles');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, [organizationId]);

  const isOnline = (vehicle: Vehicle) => {
    const ts = vehicle.last_position?.timestamp;
    if (!ts) return false;
    const lastTime = new Date(ts).getTime();
    return (Date.now() - lastTime) < 30 * 60 * 1000;
  };

  const totalCount = vehicles.length;
  const onlineCount = vehicles.filter(isOnline).length;
  const offlineCount = totalCount - onlineCount;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-sm py-2">{error}</div>;
  }

  return (
    <div className="flex flex-col">
      {/* Kompakt header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TruckIcon className="w-8 h-8 text-blue-500" />
          <div>
            <div className="text-2xl font-bold text-textDefault">{totalCount}</div>
            <div className="text-xs text-textSecondary">
              {t('vehicleListWidget.totalVehicles')}
            </div>
          </div>
        </div>
        
        <button
          onClick={fetchVehicles}
          className="p-1 hover:bg-bgElevated rounded"
          title={t('common.refresh')}
        >
          <ArrowPathIcon className={`w-4 h-4 text-textSecondary ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Online/Offline inline */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-borderDefault">
        <div className="flex items-center gap-1.5 text-green-500">
          <SignalIcon className="w-4 h-4" />
          <span className="font-semibold">{onlineCount}</span>
          <span className="text-xs text-textTertiary">online</span>
        </div>
        <div className="flex items-center gap-1.5 text-textTertiary">
          <SignalSlashIcon className="w-4 h-4" />
          <span className="font-semibold">{offlineCount}</span>
          <span className="text-xs text-textTertiary">offline</span>
        </div>
      </div>
    </div>
  );
};

export default VehicleListWidget;
